// D138 (§8.1) — the lesson picker. Follows the ControlsScreen overlay pattern: an injected Document, a
// plain data list in, callbacks out, textContent only. All lessons are OPEN from the start — order is a
// suggestion, never a gate, because frustration is the enemy of a training ground (PT4-P16).
//
// D157 (C3 interim): the six practice drills left the home screen; until the training-ground redesign
// handoff lands they live here as a plain second section, so they stay one click from home.

import { button, div, el } from './dom';
import { capsTag } from './kit';

export interface LessonCard {
  readonly id: string;
  readonly title: string;
  readonly blurb: string;
  readonly icon: string;
  readonly done: boolean;
  readonly bonus: boolean;
}

export interface DrillRow {
  readonly id: string;
  readonly label: string;
  readonly blurb: string;
}

export function lessonPicker(
  doc: Document,
  cards: readonly LessonCard[],
  onPick: (id: string) => void,
  onBack: () => void,
  drills: readonly DrillRow[] = [],
  onDrill: (id: string) => void = () => {},
): HTMLElement {
  const root = el('div', 'position:fixed;inset:0;z-index:60;overflow:auto;padding:28px 18px;background:rgba(8,11,16,.95);color:#eef3f8;font:14px/1.5 system-ui,sans-serif');
  const h = el('div', 'font-size:22px;font-weight:800;margin-bottom:4px');
  h.textContent = '🎓 Training ground';
  const sub = div('opacity:.7;margin-bottom:18px', 'Work through them in any order. Attempts are free.');
  root.append(h, sub);
  const grid = el('div', 'display:grid;grid-template-columns:repeat(auto-fill,minmax(230px,1fr));gap:12px;max-width:920px');
  for (const c of cards) {
    const card = el('div', `padding:12px 14px;border-radius:10px;background:rgba(255,255,255,.06);cursor:pointer;border:1px solid ${c.done ? 'rgba(92,255,157,.45)' : 'transparent'}`);
    const title = el('div', 'font-weight:700;margin-bottom:4px');
    title.textContent = `${c.icon} ${c.title}${c.done ? '  ✓' : ''}${c.bonus ? ' ★' : ''}`;
    const blurb = el('div', 'opacity:.75;font-size:13px');
    blurb.textContent = c.blurb;
    card.append(title, blurb);
    card.addEventListener('click', () => onPick(c.id));
    grid.append(card);
  }
  root.append(grid);
  if (drills.length > 0) {
    const section = div('margin-top:26px;max-width:920px');
    section.append(capsTag(doc, 'PRACTICE DRILLS', 'quiet'));
    section.append(div('opacity:.7;font-size:13px;margin:4px 0 10px', 'Just the situation, no checklist.'));
    const list = div('display:flex;flex-direction:column;gap:4px');
    for (const dr of drills) {
      const b = el('button', 'text-align:left;padding:6px 10px;border:1px solid var(--border-strong);background:var(--bg-keycap);color:var(--ink-1);border-radius:5px;cursor:pointer;font:inherit');
      b.textContent = `${dr.label} — ${dr.blurb}`;
      b.onclick = () => onDrill(dr.id);
      list.append(b);
    }
    section.append(list);
    root.append(section);
  }
  root.append(div('margin-top:20px'), button('← Back', onBack));
  doc.body.appendChild(root);
  return root;
}
